import "./AgentExpanded.css";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Agent, AgentDetails, fetchAgentDetails, MessageChartData, fetchAgentMessageCharts, HandoffChartData, fetchAgentHandoffCharts, ToolCallsChartData, fetchAgentToolCallsCharts, ToolCallsBreakdownData, fetchAgentToolCallsBreakdown, ErrorsChartData, fetchAgentErrorsCharts, ErrorsBreakdownData, fetchAgentErrorsBreakdown, AgentInsightData, fetchAgentInsights } from "../../api/agents";
import { useGlobal } from "../../context/GlobalContext";
import { Chart } from "../common/Chart";
import { MetricCard } from "../common/MetricCard";

interface AgentExpandedProps {
    agent: Agent;
}

function getColorForItem(item: string, index: number): string {
    const hue = (index * 137.5) % 360;
    return `hsl(${hue}, 70%, 60%)`;
}

export function AgentExpanded({ agent }: AgentExpandedProps) {
    const { selectedProjectKey, userKey } = useGlobal();
    const navigate = useNavigate();
    const [details, setDetails] = useState<AgentDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [insightsTimeRange, setInsightsTimeRange] = useState('24h');
    const [interactionsTimeRange, setInteractionsTimeRange] = useState('24h');
    const [handoffTimeRange, setHandoffTimeRange] = useState('24h');
    const [toolCallsTimeRange, setToolCallsTimeRange] = useState('24h');
    const [toolBreakdownTimeRange, setToolBreakdownTimeRange] = useState('24h');
    const [errorsTimeRange, setErrorsTimeRange] = useState('24h');
    const [errorsBreakdownTimeRange, setErrorsBreakdownTimeRange] = useState('24h');
    const [insights, setInsights] = useState<AgentInsightData>({ mostActiveTool: { name: '', count: 0 }, mostErroredTool: { name: '', count: 0 }, commonError: '' });
    const [insightsLoading, setInsightsLoading] = useState(true);
    const [chartData, setChartData] = useState<MessageChartData[]>([]);
    const [chartLoading, setChartLoading] = useState(true);
    const [handoffData, setHandoffData] = useState<HandoffChartData[]>([]);
    const [handoffLoading, setHandoffLoading] = useState(true);
    const [toolCallsData, setToolCallsData] = useState<ToolCallsChartData[]>([]);
    const [toolCallsLoading, setToolCallsLoading] = useState(true);
    const [toolBreakdownData, setToolBreakdownData] = useState<ToolCallsBreakdownData[]>([]);
    const [toolBreakdownLoading, setToolBreakdownLoading] = useState(true);
    const [errorsData, setErrorsData] = useState<ErrorsChartData[]>([]);
    const [errorsLoading, setErrorsLoading] = useState(true);
    const [errorsBreakdownData, setErrorsBreakdownData] = useState<ErrorsBreakdownData[]>([]);
    const [errorsBreakdownLoading, setErrorsBreakdownLoading] = useState(true);

    useEffect(() => {
        const loadDetails = async () => {
            if (!selectedProjectKey || !userKey) return;
            
            setLoading(true);
            try {
                const data = await fetchAgentDetails(userKey, selectedProjectKey, agent.agentName);
                setDetails(data);
            } catch (err) {
                console.error('Failed to load agent details:', err);
            } finally {
                setLoading(false);
            }
        };
        loadDetails();
    }, [agent.agentName, selectedProjectKey, userKey]);

    useEffect(() => {
        const loadInsights = async () => {
            if (!selectedProjectKey || !userKey) return;
            
            setInsightsLoading(true);
            try {
                const data = await fetchAgentInsights(userKey, selectedProjectKey, agent.agentName, insightsTimeRange);
                setInsights(data);
            } catch (err) {
                console.error('Failed to load insights:', err);
            } finally {
                setInsightsLoading(false);
            }
        };
        loadInsights();
    }, [agent.agentName, selectedProjectKey, userKey, insightsTimeRange]);

    useEffect(() => {
        const loadChartData = async () => {
            if (!selectedProjectKey || !userKey) return;
            
            setChartLoading(true);
            try {
                const data = await fetchAgentMessageCharts(userKey, selectedProjectKey, agent.agentName, interactionsTimeRange);
                setChartData(data);
            } catch (err) {
                console.error('Failed to load chart data:', err);
            } finally {
                setChartLoading(false);
            }
        };
        loadChartData();
    }, [agent.agentName, selectedProjectKey, userKey, interactionsTimeRange]);

    useEffect(() => {
        const loadHandoffData = async () => {
            if (!selectedProjectKey || !userKey) return;
            
            setHandoffLoading(true);
            try {
                const data = await fetchAgentHandoffCharts(userKey, selectedProjectKey, agent.agentName, handoffTimeRange);
                setHandoffData(data);
            } catch (err) {
                console.error('Failed to load handoff data:', err);
            } finally {
                setHandoffLoading(false);
            }
        };
        loadHandoffData();
    }, [agent.agentName, selectedProjectKey, userKey, handoffTimeRange]);

    useEffect(() => {
        const loadToolCallsData = async () => {
            if (!selectedProjectKey || !userKey) return;
            
            setToolCallsLoading(true);
            try {
                const data = await fetchAgentToolCallsCharts(userKey, selectedProjectKey, agent.agentName, toolCallsTimeRange);
                setToolCallsData(data);
            } catch (err) {
                console.error('Failed to load tool calls data:', err);
            } finally {
                setToolCallsLoading(false);
            }
        };
        loadToolCallsData();
    }, [agent.agentName, selectedProjectKey, userKey, toolCallsTimeRange]);

    useEffect(() => {
        const loadToolBreakdownData = async () => {
            if (!selectedProjectKey || !userKey) return;
            
            setToolBreakdownLoading(true);
            try {
                const data = await fetchAgentToolCallsBreakdown(userKey, selectedProjectKey, agent.agentName, toolBreakdownTimeRange);
                // Fill missing tools with 0
                const normalizedData = data.map(item => {
                    const normalized: any = { timestamp: item.timestamp };
                    details?.tools?.forEach(tool => {
                        normalized[tool] = item[tool] || 0;
                    });
                    return normalized;
                });
                setToolBreakdownData(normalizedData);
            } catch (err) {
                console.error('Failed to load tool breakdown data:', err);
            } finally {
                setToolBreakdownLoading(false);
            }
        };
        loadToolBreakdownData();
    }, [agent.agentName, selectedProjectKey, userKey, toolBreakdownTimeRange, details?.tools]);

    useEffect(() => {
        const loadErrorsData = async () => {
            if (!selectedProjectKey || !userKey) return;
            
            setErrorsLoading(true);
            try {
                const data = await fetchAgentErrorsCharts(userKey, selectedProjectKey, agent.agentName, errorsTimeRange);
                setErrorsData(data);
            } catch (err) {
                console.error('Failed to load errors data:', err);
            } finally {
                setErrorsLoading(false);
            }
        };
        loadErrorsData();
    }, [agent.agentName, selectedProjectKey, userKey, errorsTimeRange]);

    useEffect(() => {
        const loadErrorsBreakdownData = async () => {
            if (!selectedProjectKey || !userKey) return;
            
            setErrorsBreakdownLoading(true);
            try {
                const data = await fetchAgentErrorsBreakdown(userKey, selectedProjectKey, agent.agentName, errorsBreakdownTimeRange);
                // Fill missing tools with 0
                const normalizedData = data.map(item => {
                    const normalized: any = { timestamp: item.timestamp };
                    details?.tools?.forEach(tool => {
                        normalized[tool] = item[tool] || 0;
                    });
                    return normalized;
                });
                setErrorsBreakdownData(normalizedData);
            } catch (err) {
                console.error('Failed to load errors breakdown data:', err);
            } finally {
                setErrorsBreakdownLoading(false);
            }
        };
        loadErrorsBreakdownData();
    }, [agent.agentName, selectedProjectKey, userKey, errorsBreakdownTimeRange, details?.tools]);

    if (loading) {
        return <div className="agent-loading">Loading agent details...</div>;
    }

    return (
        <div className="agent-expanded-page">
            <div className="agent-expanded-header">
                <h1 className="agent-expanded-title">{agent.agentName}</h1>
                <button className="back-button" onClick={() => navigate('/agents')}>
                    ← Back to all agents
                </button>
            </div>

            <div className="top-section">
                <div className="agent-details-card">
                    <h2 className="section-title">Agent Details</h2>
                    
                    <div className="detail-section">
                        <h3 className="detail-label">Instruction Set</h3>
                        <p className="detail-text instructions-text">
                            {details?.instructions || 'No instructions available'}
                        </p>
                    </div>

                    <div className="detail-section">
                        <h3 className="detail-label">Model</h3>
                        <p className="detail-text">{details?.model || agent.model}</p>
                    </div>

                    <div className="detail-section">
                        <h3 className="detail-label">Sub-agents for handoff</h3>
                        <div className="tags-list">
                            {details?.handoffs && details.handoffs.length > 0 ? (
                                details.handoffs.map((handoff, index) => (
                                    <span key={handoff} className="tag">
                                        <span 
                                            className="tag-color-indicator"
                                            style={{ backgroundColor: getColorForItem(handoff, index) }}
                                        />
                                        {handoff}
                                    </span>
                                ))
                            ) : (
                                <span className="tag">None</span>
                            )}
                        </div>
                    </div>

                    <div className="detail-section">
                        <h3 className="detail-label">Tools available to agent</h3>
                        <div className="tags-list">
                            {details?.tools && details.tools.length > 0 ? (
                                details.tools.map((tool, index) => (
                                    <span key={tool} className="tag">
                                        <span 
                                            className="tag-color-indicator"
                                            style={{ backgroundColor: getColorForItem(tool, index) }}
                                        />
                                        {tool}
                                    </span>
                                ))
                            ) : (
                                <span className="tag">None</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="chart-card">
                    <div className="chart-header">
                        <h3 className="section-title">Interactions Over Time</h3>
                        <div className="time-range-selector">
                            {['1h', '8h', '24h', '7d', '15d', '30d'].map(range => (
                                <button
                                    key={range}
                                    className={`time-range-btn ${interactionsTimeRange === range ? 'active' : ''}`}
                                    onClick={() => setInteractionsTimeRange(range)}
                                >
                                    {range}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    <div className="charts-wrapper">
                        <div className="chart-section">
                            <h3 className="chart-title">User Messages</h3>
                            <Chart
                                data={chartData}
                                series={[{ dataKey: 'user', name: 'User Messages', color: '#f59e0b' }]}
                                height={180}
                                showAxes={true}
                                loading={chartLoading}
                            />
                        </div>
                        
                        <div className="chart-section">
                            <h3 className="chart-title">Agent Messages</h3>
                            <Chart
                                data={chartData}
                                series={[{ dataKey: 'assistant', name: 'Assistant Messages', color: '#10b981' }]}
                                height={180}
                                showAxes={true}
                                loading={chartLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="insights-section">
                <div className="insights-header">
                    <h2 className="section-title">Insights</h2>
                    <div className="time-range-selector">
                        {['1h', '8h', '24h', '7d', '15d', '30d'].map(range => (
                            <button
                                key={range}
                                className={`time-range-btn ${insightsTimeRange === range ? 'active' : ''}`}
                                onClick={() => setInsightsTimeRange(range)}
                            >
                                {range}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="insights-grid">
                    <MetricCard title="Most Active Tool" accentColor="#4ade80" loading={insightsLoading}>
                        <div className="insight-name">{insights.mostActiveTool.name}</div>
                        <div className="insight-detail">Calls: <span>{insights.mostActiveTool.count.toLocaleString()}</span></div>
                    </MetricCard>

                    <MetricCard title="Most Errored Tool" accentColor="#f87171" loading={insightsLoading}>
                        <div className="insight-name">{insights.mostErroredTool.name}</div>
                        <div className="insight-detail">Errors: <span>{insights.mostErroredTool.count.toLocaleString()}</span></div>
                    </MetricCard>

                    <MetricCard title="Most Common Error Message" accentColor="#fbbf24" loading={insightsLoading}>
                        <div className="insight-name-with-copy">
                            <span className="insight-name">
                                {insights.commonError.length > 40 ? insights.commonError.slice(0, 40) + "..." : insights.commonError}
                            </span>
                            <button 
                                className="copy-btn"
                                onClick={() => navigator.clipboard.writeText(insights.commonError)}
                                title="Copy full message"
                            >
                                Copy
                            </button>
                        </div>
                    </MetricCard>
                </div>
            </div>

            <div className="charts-grid">
                <div className="chart-card">
                    <div className="chart-header">
                        <h3 className="section-title">Total Handoffs Over Time</h3>
                        <div className="time-range-selector">
                            {['1h', '8h', '24h', '7d', '15d', '30d'].map(range => (
                                <button
                                    key={range}
                                    className={`time-range-btn ${handoffTimeRange === range ? 'active' : ''}`}
                                    onClick={() => setHandoffTimeRange(range)}
                                >
                                    {range}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Chart
                        data={handoffData}
                        series={[{ dataKey: 'handoffs', name: 'Handoffs', color: '#8b5cf6' }]}
                        height={280}
                        showAxes={true}
                        loading={handoffLoading}
                    />
                </div>
                <div className="chart-placeholder">
                    <h3>Handoff Activity per Agent</h3>
                    <p>Chart coming soon</p>
                </div>
            </div>

            <div className="charts-grid">
                <div className="chart-card">
                    <div className="chart-header">
                        <h3 className="section-title">Total Tool Calls Over Time</h3>
                        <div className="time-range-selector">
                            {['1h', '8h', '24h', '7d', '15d', '30d'].map(range => (
                                <button
                                    key={range}
                                    className={`time-range-btn ${toolCallsTimeRange === range ? 'active' : ''}`}
                                    onClick={() => setToolCallsTimeRange(range)}
                                >
                                    {range}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Chart
                        data={toolCallsData}
                        series={[{ dataKey: 'toolCalls', name: 'Tool Calls', color: '#06b6d4' }]}
                        height={280}
                        showAxes={true}
                        loading={toolCallsLoading}
                    />
                </div>
                <div className="chart-card">
                    <div className="chart-header">
                        <h3 className="section-title">Activity per Tool</h3>
                        <div className="time-range-selector">
                            {['1h', '8h', '24h', '7d', '15d', '30d'].map(range => (
                                <button
                                    key={range}
                                    className={`time-range-btn ${toolBreakdownTimeRange === range ? 'active' : ''}`}
                                    onClick={() => setToolBreakdownTimeRange(range)}
                                >
                                    {range}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Chart
                        data={toolBreakdownData}
                        series={
                            details?.tools && details.tools.length > 0
                                ? details.tools.map((tool, index) => ({
                                    dataKey: tool,
                                    name: tool,
                                    color: getColorForItem(tool, index)
                                }))
                                : []
                        }
                        height={280}
                        showAxes={true}
                        showLegend={true}
                        loading={toolBreakdownLoading}
                    />
                </div>
            </div>

            <div className="charts-grid">
                <div className="chart-card">
                    <div className="chart-header">
                        <h3 className="section-title">Total Errors Over Time</h3>
                        <div className="time-range-selector">
                            {['1h', '8h', '24h', '7d', '15d', '30d'].map(range => (
                                <button
                                    key={range}
                                    className={`time-range-btn ${errorsTimeRange === range ? 'active' : ''}`}
                                    onClick={() => setErrorsTimeRange(range)}
                                >
                                    {range}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Chart
                        data={errorsData}
                        series={[{ dataKey: 'errors', name: 'Errors', color: '#ef4444' }]}
                        height={280}
                        showAxes={true}
                        loading={errorsLoading}
                    />
                </div>
                <div className="chart-card">
                    <div className="chart-header">
                        <h3 className="section-title">Errors per Tool</h3>
                        <div className="time-range-selector">
                            {['1h', '8h', '24h', '7d', '15d', '30d'].map(range => (
                                <button
                                    key={range}
                                    className={`time-range-btn ${errorsBreakdownTimeRange === range ? 'active' : ''}`}
                                    onClick={() => setErrorsBreakdownTimeRange(range)}
                                >
                                    {range}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Chart
                        data={errorsBreakdownData}
                        series={
                            details?.tools && details.tools.length > 0
                                ? details.tools.map((tool, index) => ({
                                    dataKey: tool,
                                    name: tool,
                                    color: getColorForItem(tool, index)
                                }))
                                : []
                        }
                        height={280}
                        showAxes={true}
                        showLegend={true}
                        loading={errorsBreakdownLoading}
                    />
                </div>
            </div>
        </div>
    );
}
